import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { InvoiceBadge, InvoiceDocument } from '../components/Invoice.js';
import { Button, LoadError, Loading, Notice } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { useMe } from '../session.js';

/** One invoice of the customer, printable; an open order can be cancelled (T-48, invoice.html). */
export function InvoicePage() {
  const { invoiceId = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const client = useQueryClient();
  const timeZone = useMe().data?.account.timezone;
  const [confirming, setConfirming] = useState(false);
  // The question takes the focus; on second thought it goes back to the button (one step).
  const ask = useRef<HTMLDivElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const printButton = useRef<HTMLButtonElement>(null);
  const [kept, setKept] = useState(false);
  useEffect(() => {
    if (confirming) ask.current?.focus();
    else if (kept) cancelButton.current?.focus();
  }, [confirming, kept]);
  const invoice = useQuery({
    queryKey: ['billing', 'invoice', invoiceId],
    queryFn: () =>
      unwrap(api.GET('/billing/invoices/{invoiceId}', { params: { path: { invoiceId } } })),
  });
  const cancel = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/billing/invoices/{invoiceId}/cancel', { params: { path: { invoiceId } } })),
    onSuccess: (data) => {
      client.setQueryData(['billing', 'invoice', invoiceId], data);
      void client.invalidateQueries({ queryKey: ['billing'], exact: true });
      void client.invalidateQueries({ queryKey: ['billing', 'invoices'] });
      setConfirming(false);
      // The question and its button are gone: the focus stays with the actions.
      printButton.current?.focus();
    },
  });

  let content;
  if (invoice.isPending) content = <Loading />;
  else if (invoice.isError)
    content = (
      <LoadError message={errorMessage(invoice.error, t)} onRetry={() => void invoice.refetch()} />
    );
  else {
    const inv = invoice.data;
    const cancellable = inv.status === 'open' && inv.method === 'bank_transfer';
    content = (
      <div className="acct">
        {params.get('ordered') && inv.status === 'open' ? (
          <Notice kind="success" title={t('invoice.orderedTitle')}>
            {t('invoice.orderedText')}
          </Notice>
        ) : null}
        <div className="inv-actions">
          <Button ref={printButton} variant="secondary" size="sm" onClick={() => window.print()}>
            {t('invoice.print')}
          </Button>
          {cancellable && !confirming ? (
            <Button
              ref={cancelButton}
              variant="ghost"
              size="sm"
              onClick={() => {
                setKept(false);
                setConfirming(true);
              }}
            >
              {t('invoice.cancel')}
            </Button>
          ) : null}
          {/* A status: the change after cancelling is read out. */}
          <span role="status">
            <InvoiceBadge invoice={inv} />
          </span>
        </div>
        {cancellable && confirming ? (
          <div ref={ask} tabIndex={-1} className="confirm">
            <b>{t('invoice.cancelConfirm')}</b>
            <div className="acts">
              <Button
                variant="danger"
                size="sm"
                busy={cancel.isPending}
                onClick={() => cancel.mutate()}
              >
                {t('invoice.cancelYes')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setKept(true);
                  setConfirming(false);
                }}
              >
                {t('invoice.keep')}
              </Button>
            </div>
          </div>
        ) : null}
        {cancel.isError ? <Notice kind="error">{errorMessage(cancel.error, t)}</Notice> : null}
        <InvoiceDocument invoice={inv} timeZone={timeZone} />
      </div>
    );
  }

  return (
    <div className="page invoice-page">
      <div className="page-title">
        <div>
          <Link className="back" to="/billing">
            {t('billing.back')}
          </Link>
          <h1>
            {invoice.data
              ? t('invoice.pageTitle', { number: invoice.data.number })
              : t('invoice.title')}
          </h1>
        </div>
      </div>
      {content}
    </div>
  );
}
